import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Target, BookOpen, Heart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { FormationCard } from '../components/FormationCard';
import { QualiopiTeaser } from '../components/QualiopiTeaser';
import { SocialProof } from '../components/SocialProof';
import { Cap360 } from '../components/Cap360';
import { FORMATIONS } from '../data/constants';
import { Badge } from '../components/ui/Badge';
export function Home() {
  return <div className="flex flex-col w-full" data-id="element-1596">
      {/* Hero Section */}
      <section className="relative bg-lavande pt-24 pb-40 overflow-hidden" data-id="element-1597">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-multiply" data-id="element-1598"></div>
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] bg-primary rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1599" />
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1600" />

        <div className="container relative z-10 text-center max-w-4xl mx-auto" data-id="element-1601">
          <Badge className="mb-8 bg-white text-brand-orange border-none shadow-sm text-sm px-5 py-2 font-bold uppercase tracking-widest" data-id="element-1602">
            Centre de Formation d'Apprentis à Joigny
          </Badge>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-primary-dark mb-8 tracking-tight leading-[1.1]" data-id="element-1603">
            Révélez votre potentiel.
            <br data-id="element-1604" />
            <span className="text-primary" data-id="element-1605">Construisez votre avenir.</span>
          </h1>
          <p className="text-xl md:text-2xl text-primary-dark/80 mb-12 max-w-2xl mx-auto leading-relaxed font-medium" data-id="element-1606">
            L'alternance avec Zillenium : une pédagogie par l'action, un
            accompagnement sur-mesure et un tremplin direct vers l'emploi.
          </p>
          <Button size="lg" className="group/btn bg-primary hover:bg-primary-dark text-white text-lg px-8 py-7 shadow-xl" asChild data-id="element-1607">
            <Link to="/le-cfa" data-id="element-1608">
              Découvrir le CFA
              <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" data-id="element-1609" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Dual Entry Block (Overlapping Hero) */}
      <section className="container relative z-20 -mt-24 mb-32" data-id="element-1610">
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-1611">
          {/* Candidat Card */}
          <Link to="/candidater" className="group relative block outline-none" data-id="element-1612">
            <div className="absolute inset-0 bg-brand-orange/5 rounded-[2.5rem] transform transition-transform duration-500 group-hover:scale-[1.02] group-hover:bg-brand-orange/10" data-id="element-1613" />
            <Card className="relative h-full border-none shadow-xl hover:shadow-2xl transition-all duration-500 rounded-[2.5rem] bg-white/90 backdrop-blur-sm overflow-hidden" data-id="element-1614">
              <CardContent className="p-10 md:p-12 flex flex-col h-full" data-id="element-1615">
                <div className="flex justify-between items-start mb-8" data-id="element-1616">
                  <div className="w-16 h-16 bg-brand-orange/10 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" data-id="element-1617">
                    <Users className="w-8 h-8 text-brand-orange" data-id="element-1618" />
                  </div>
                  <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-orange group-hover:border-brand-orange group-hover:text-white text-slate-400" data-id="element-1619">
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-45" data-id="element-1620" />
                  </div>
                </div>
                <h2 className="text-3xl font-extrabold font-heading mb-4 text-primary-dark transition-colors group-hover:text-brand-orange" data-id="element-1621">
                  Je suis candidat
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed flex-1" data-id="element-1622">
                  Trouvez votre voie, formez-vous à un métier d'avenir et
                  obtenez un diplôme d'État ou un titre professionnel reconnu,
                  tout en étant rémunéré.
                </p>
                <div className="mt-8 font-bold text-brand-orange flex items-center" data-id="element-1623">
                  Candidater maintenant
                  <div className="h-px bg-brand-orange flex-1 ml-4 opacity-0 transition-opacity duration-300 group-hover:opacity-30" data-id="element-1624" />
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Entreprise Card */}
          <Link to="/entreprises" className="group relative block outline-none" data-id="element-1625">
            <div className="absolute inset-0 bg-primary/20 rounded-[2.5rem] transform transition-transform duration-500 group-hover:scale-[1.02] group-hover:bg-primary/30" data-id="element-1626" />
            <Card className="relative h-full border-none shadow-xl hover:shadow-2xl transition-all duration-500 rounded-[2.5rem] bg-primary-dark overflow-hidden" data-id="element-1627">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary rounded-full blur-[80px] opacity-20 transition-opacity duration-500 group-hover:opacity-40 pointer-events-none" data-id="element-1628" />
              <CardContent className="p-10 md:p-12 flex flex-col h-full relative z-10" data-id="element-1629">
                <div className="flex justify-between items-start mb-8" data-id="element-1630">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" data-id="element-1631">
                    <Target className="w-8 h-8 text-lavande" data-id="element-1632" />
                  </div>
                  <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:border-white group-hover:text-primary-dark text-white/50" data-id="element-1633">
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-45" data-id="element-1634" />
                  </div>
                </div>
                <h2 className="text-3xl font-extrabold font-heading mb-4 text-white transition-colors group-hover:text-lavande" data-id="element-1635">
                  Je suis une entreprise
                </h2>
                <p className="text-lavande text-lg leading-relaxed flex-1" data-id="element-1636">
                  Recrutez vos futurs talents, formez-les à vos méthodes et
                  bénéficiez d'un accompagnement complet dans vos démarches.
                </p>
                <div className="mt-8 font-bold text-white flex items-center" data-id="element-1637">
                  Recruter un alternant
                  <div className="h-px bg-white flex-1 ml-4 opacity-0 transition-opacity duration-300 group-hover:opacity-30" data-id="element-1638" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Pourquoi choisir Zillenium */}
      <section className="py-24 bg-muted relative overflow-hidden" data-id="element-1639">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-1640" />
        <div className="container relative z-10" data-id="element-1641">
          <SectionHeading title="Pourquoi choisir Zillenium Alternance" subtitle="Un CFA à taille humaine, engagé pour la réussite de ses apprentis et la satisfaction de ses entreprises partenaires." centered data-id="element-1642" />

          <div className="grid md:grid-cols-3 gap-12 mt-20" data-id="element-1643">
            <div className="text-center group" data-id="element-1644">
              <div className="w-20 h-20 bg-white shadow-md rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-1645">
                <Users className="w-10 h-10 text-primary animate-soft-bounce" data-id="element-1646" />
              </div>
              <h3 className="text-2xl font-bold font-heading mb-4 text-primary-dark" data-id="element-1647">
                Accompagnement individualisé
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4" data-id="element-1648">
                Des effectifs réduits pour un suivi personnalisé, de la
                recherche d'entreprise jusqu'à l'obtention du diplôme.
              </p>
              <Link to="/candidater#cap-360-section" className="inline-flex items-center text-brand-orange font-bold hover:text-primary transition-colors" data-id="element-1649">
                Découvrir CAP 360 <ArrowRight className="w-4 h-4 ml-1" data-id="element-1650" />
              </Link>
            </div>
            <div className="text-center group" data-id="element-1651">
              <div className="w-20 h-20 bg-white shadow-md rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-1652">
                <BookOpen className="w-10 h-10 text-primary" data-id="element-1653" />
              </div>
              <h3 className="text-2xl font-bold font-heading mb-4 text-primary-dark" data-id="element-1654">
                Information vérifiable
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed" data-id="element-1655">
                Une transparence totale sur nos résultats, nos méthodes et nos
                engagements envers nos parties prenantes.
              </p>
            </div>
            <div className="text-center group" data-id="element-1656">
              <div className="w-20 h-20 bg-white shadow-md rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:-translate-y-2 transition-transform duration-300" data-id="element-1657">
                <CheckCircle2 className="w-10 h-10 text-brand-orange" data-id="element-1658" />
              </div>
              <h3 className="text-2xl font-bold font-heading mb-4 text-primary-dark" data-id="element-1659">
                Certifié Qualiopi
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed" data-id="element-1660">
                Une démarche qualité reconnue par l'État, garantissant le
                sérieux et la conformité de nos actions de formation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Teaser BDE */}
      <section className="py-16 bg-primary-dark text-white relative overflow-hidden" data-id="element-1661">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/4 w-[300px] h-[300px] bg-brand-orange rounded-full blur-[80px] opacity-30 pointer-events-none" data-id="element-1662" />
        <div className="container relative z-10" data-id="element-1663">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-white/5 p-8 md:p-10 rounded-3xl border border-white/10" data-id="element-1664">
            <div className="flex items-center gap-6" data-id="element-1665">
              <div className="w-16 h-16 bg-brand-orange rounded-2xl flex items-center justify-center shrink-0" data-id="element-1666">
                <Heart className="w-8 h-8 text-white animate-soft-pulse" data-id="element-1667" />
              </div>
              <div data-id="element-1668">
                <h3 className="font-extrabold font-heading text-2xl mb-2" data-id="element-1669">
                  BDE Zillenium
                </h3>
                <p className="text-lavande text-lg" data-id="element-1670">
                  Une vie étudiante dynamique, conviviale et engagée.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0" data-id="element-1671">
              <Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark" asChild data-id="element-1672">
                <Link to="/le-cfa" data-id="element-1673">En savoir plus</Link>
              </Button>
              <Button className="bg-brand-orange hover:bg-[#E67E22] text-white" asChild data-id="element-1674">
                <a href="https://www.instagram.com/bdezillenium/" target="_blank" rel="noopener noreferrer" data-id="element-1675">
                  Suivre sur Instagram
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Nos formations */}
      <section className="py-32 bg-white" data-id="element-1676">
        <div className="container" data-id="element-1677">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8" data-id="element-1678">
            <SectionHeading title="Nos formations en alternance" subtitle="Un diplôme d'État (BTS) et un titre professionnel du Ministère du Travail, tous deux inscrits au RNCP, pour répondre aux besoins réels des entreprises du territoire." className="mb-0 max-w-2xl" data-id="element-1679" />
            <Button size="lg" variant="outline" className="hidden md:flex" asChild data-id="element-1680">
              <Link to="/formations" data-id="element-1681">
                Voir toutes les formations{' '}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-1682" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-10" data-id="element-1683">
            {FORMATIONS.map(formation => <FormationCard key={formation.id} formation={formation} data-id="element-1684" />)}
          </div>

          <div className="mt-10 max-w-3xl mx-auto" data-id="element-1685">
            <Cap360 data-id="element-1686" />
          </div>

          <div className="mt-12 text-center md:hidden" data-id="element-1687">
            <Button size="lg" variant="outline" className="w-full" asChild data-id="element-1688">
              <Link to="/formations" data-id="element-1689">Voir toutes les formations</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <SocialProof data-id="element-1690" />

      {/* Qualiopi Teaser */}
      <section className="py-32 bg-lavande relative overflow-hidden" data-id="element-1691">
        <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-white rounded-full blur-[120px] opacity-60 pointer-events-none" data-id="element-1692" />
        <div className="container max-w-6xl relative z-10" data-id="element-1693">
          <QualiopiTeaser data-id="element-1694" />
        </div>
      </section>

      {/* Entreprises Teaser */}
      <section className="py-32 bg-primary-dark text-white relative overflow-hidden" data-id="element-1695">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary rounded-full blur-[100px] opacity-40 -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-1696" />
        <div className="container text-center max-w-4xl mx-auto relative z-10" data-id="element-1697">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold mb-8 tracking-tight leading-[1.1]" data-id="element-1698">
            Entreprises : recrutez vos futurs talents
          </h2>
          <p className="text-lavande text-xl md:text-2xl mb-12 leading-relaxed font-medium" data-id="element-1699">
            Confiez-nous vos besoins en recrutement. Nous sourçons,
            présélectionnons et formons les profils adaptés à votre
            développement commercial.
          </p>
          <Button size="lg" className="bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1700">
            <Link to="/entreprises" data-id="element-1701">Découvrir notre offre entreprise</Link>
          </Button>
        </div>
      </section>
    </div>;
}